import React, { useCallback, useMemo, useState, useDeferredValue } from "react";
import { Platform, View } from "react-native";
import * as Clipboard from "expo-clipboard";
import {
  AHSeparator,
  Box,
  CategorySelectorCompact,
  DialogButton,
  getTodoCategoryIcon,
  Input,
  ListItem,
  useTheme,
} from "arkh";

import { FormattedMessage, useIntl } from "react-intl";
import { useShowCancelWarningAlert } from "../../../common/useCancelWarning";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { StoreAdminNavigationScreenProps } from "../../StoreAdmin/StoreAdminNavigation";

import { EntityHeader } from "@/src/components/EntityHeader";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { usePreventRemove } from "@react-navigation/native";
import { DialogButtonTeams } from "widgets/src/DialogButtonTeams";
import { TeamSelectPopover } from "widgets/src/TeamSelectPopover";
import {
  ConnectAdminInboxQuery,
  ConnectInboxAudience,
} from "./__generated__/ConnectAdminInboxQuery.graphql";
import { onShareQR } from "../onShareQR";
import { TodoToolbarButtonTags } from "widgets";

import { SvgFromXml } from "react-native-svg";
import { TodoCategoryDialog } from "./TodoCategoryDialog";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { WebPreview } from "./WebPreview";

import { Header } from "@react-navigation/elements";
import { HeaderButton, HeaderButtonLoading } from "@/src/components";
import { MoreButton, MoreItem } from "@/src/common/MoreButton";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useAlert } from "@/src/common/Alert";
import { ConnectInboxUpdateInput } from "./__generated__/ConnectAdminInboxSaveMutation.graphql";
import { TodoTagsDialog } from "widgets/src/Todo/TodoDialogs/TodoTagsDialog";

type Props = StoreAdminNavigationScreenProps<"ConnectAdminInbox">;

export function ConnectAdminInboxScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <ConnectAdminInput {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}
type ContactFieldConfig = "REQUIRED" | "OPTIONAL" | "DISABLED";

function hasActiveContactFields(fields: (ContactFieldConfig | null)[]) {
  return fields.some((field) => field !== null && field !== "DISABLED");
}

function ConnectAdminInput(props: Props & { fetchKey: string }) {
  const [featuresOpen, setFeaturesOpen] = useState<
    "tags" | "teams" | "todoCategory" | null
  >(null);

  const data = useLazyLoadQuery<ConnectAdminInboxQuery>(
    graphql`
      query ConnectAdminInboxQuery($id: ID!) {
        store {
          id
          publicTeams {
            id
            name
            ...TeamSelectList_Team
            ...DialogButtonTeams_teams
          }
          todoCategories(includeDefault: true) {
            id
            title
            icon
            tags {
              id
              title
              color
              ...TodoToolbarButtonTags_tags
            }
          }
        }
        connectInbox(id: $id) @required(action: THROW) {
          id
          audience

          teams {
            id
          }
          tags {
            id
          }
          todoCategory {
            id
          }

          welcomeMessage

          customTitle
          description

          successMessage

          publicUrl
          publicQRCode {
            svg
          }

          contactFormName
          contactFormEmail
          contactFormPhone
          contactFormPrompt
        }
      }
    `,
    {
      id: props.route.params.inboxId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[] | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[] | null>(null);
  const [_title, setTitle] = useState<string | null>(null);
  const [_description, setDescription] = useState<string | null>(null);
  const [_welcomeMessage, setWelcomeMessage] = useState<string | null>(null);
  const [_successMessage, setSuccessMessage] = useState<string | null>(null);
  const [_todoCategoryId, setTodoCategoryId] = useState<string | null>(null);
  const [_audience, setAudience] = useState<ConnectInboxAudience | null>(null);

  const [_contactFieldPrompt, setContactFieldPrompt] = useState<string | null>(
    null
  );
  const [_contactFormPhone, setContactFormPhone] =
    useState<ContactFieldConfig | null>(null);
  const [_contactFormEmail, setContactFormEmail] =
    useState<ContactFieldConfig | null>(null);
  const [_contactFormName, setContactFormName] =
    useState<ContactFieldConfig | null>(null);

  function resetState() {
    setSelectedTeamIds(null);
    setTitle(null);
    setDescription(null);
    setSuccessMessage(null);
    setSelectedTagIds(null);
    setTodoCategoryId(null);
    setAudience(null);
    setWelcomeMessage(null);

    setContactFieldPrompt(null);
    setContactFormPhone(null);
    setContactFormEmail(null);
    setContactFormName(null);
  }

  const hasContactFields = hasActiveContactFields([
    _contactFormName ?? data.connectInbox.contactFormName ?? null,
    _contactFormPhone ?? data.connectInbox.contactFormPhone ?? null,
    _contactFormEmail ?? data.connectInbox.contactFormEmail ?? null,
  ]);
  const teamIdsToFilter =
    selectedTeamIds ?? data?.connectInbox.teams.map((team) => team.id);
  const tagIdsToFilter =
    selectedTagIds ?? data.connectInbox.tags.map((tag) => tag.id);

  const [deleteMutation, deleteLoading] = useMutation(graphql`
    mutation ConnectAdminInboxDeleteInboxMutation($id: ID!) {
      store {
        connectInbox(id: $id) {
          delete {
            connectInbox {
              id @deleteRecord
            }
          }
        }
      }
    }
  `);
  const [save, saveLoading] = useMutation(graphql`
    mutation ConnectAdminInboxSaveMutation(
      $id: ID!
      $input: ConnectInboxUpdateInput!
    ) {
      store {
        connectInbox(id: $id) {
          update(input: $input) {
            connectInbox {
              audience
              customTitle
              description
              successMessage
              welcomeMessage

              contactFormName
              contactFormEmail
              contactFormPhone
              contactFormPrompt

              teams {
                id
              }
              tags {
                id
              }
              todoCategory {
                id
              }
            }
          }
        }
      }
    }
  `);

  const input = useMemo(
    (): ConnectInboxUpdateInput => ({
      setTeamIds: selectedTeamIds,
      setCustomTitle: _title,
      setDescription: _description,
      setSuccessMessage: _successMessage,
      setTagIds: selectedTagIds,
      setTodoCategoryId: _todoCategoryId,
      setAudience: _audience,
      setWelcomeMessage: _welcomeMessage,

      setContactFormPrompt: _contactFieldPrompt,
      setContactFormPhone: _contactFormPhone,
      setContactFormEmail: _contactFormEmail,
      setContactFormName: _contactFormName,
    }),
    [
      selectedTeamIds,
      _title,
      _description,
      _successMessage,
      selectedTagIds,
      _todoCategoryId,
      _audience,
      _welcomeMessage,
      _contactFieldPrompt,
      _contactFormPhone,
      _contactFormEmail,
      _contactFormName,
    ]
  );

  const isDirty = useMemo(() => {
    for (const [, value] of Object.entries(input)) {
      if (value !== null) {
        return true;
      }
    }
    return false;
  }, [input]);
  const [_saveKey, setSaveKey] = useState(0);

  const saveKey = useDeferredValue(_saveKey);

  const onSave = useCallback(
    () =>
      save({
        variables: {
          id: props.route.params.inboxId,
          input,
        },
        onCompleted: () => {
          resetState();
          setSaveKey((prev) => prev + 1);
        },
      }),
    [input, props.route.params.inboxId, save]
  );

  const showCancelWarningAlert = useShowCancelWarningAlert();

  const selectedTeams = useMemo(
    () =>
      data?.store.publicTeams
        .filter((team) => teamIdsToFilter.includes(team.id))
        .sort((a, b) => (a.name > b.name ? 1 : -1)) ?? [],
    [data?.store.publicTeams, teamIdsToFilter]
  );

  const selectedTodoCategoryId = useMemo(() => {
    return _todoCategoryId ?? data.connectInbox.todoCategory?.id;
  }, [_todoCategoryId, data.connectInbox.todoCategory?.id]);

  const selectedTodoCategory = useMemo(() => {
    return data.store.todoCategories.find(
      (category) => category.id === selectedTodoCategoryId
    );
  }, [data.store.todoCategories, selectedTodoCategoryId]);

  const selectedTags = useMemo(() => {
    return (
      selectedTodoCategory?.tags.filter((tag) =>
        tagIdsToFilter.includes(tag.id)
      ) ?? []
    );
  }, [selectedTodoCategory?.tags, tagIdsToFilter]);

  const publicUrl = data.connectInbox.publicUrl;

  const onShare = useCallback(() => {
    if (data.connectInbox.publicQRCode?.svg) {
      onShareQR(data.connectInbox.publicQRCode?.svg);
    }
  }, [data.connectInbox.publicQRCode?.svg]);

  const showPreview = useCallback(() => {
    props.navigation.navigate("WebView", {
      url: publicUrl,
    });
  }, [props.navigation, publicUrl]);

  const audience = useMemo(() => {
    return _audience ?? data.connectInbox.audience;
  }, [_audience, data.connectInbox.audience]);

  const intl = useIntl();
  const welcomeMessagePlaceholder = intl.formatMessage({
    defaultMessage: "How can we improve?",
    description: "Connect Admin Input: Welcome message Placeholder",
  });
  const descriptionPlaceholder = intl.formatMessage({
    defaultMessage: "Help us be better, by telling us what you think.",
    description: "Connect Admin Input: Description Placeholder",
  });
  const successMessagePlaceholder = intl.formatMessage({
    defaultMessage: "Thank you for your feedback!",
    description: "Connect Admin Input: Success message Placeholder",
  });

  const customerAudience = intl.formatMessage({
    defaultMessage: "Customers",
    description: "Connect Admin Input: Customer Audience",
  });
  const employeeAudience = intl.formatMessage({
    defaultMessage: "Employees",
    description: "Connect Admin Input: Employee Audience",
  });

  const contactFieldPrompt = intl.formatMessage({
    defaultMessage:
      "Leave your contact information and we'll get back to you as soon as possible.",
    description: "Connect Admin Input: Contact Field Prompt",
  });

  const theme = useTheme();
  const intl_tokens = useIntlTokens();
  const Alert = useAlert();
  const moreButtonItems = useMemo(
    (): MoreItem[] => [
      {
        label: intl_tokens.Delete,
        icon: "trash",
        color: "negative",
        type: "button",
        onPress: () => {
          Alert.alert(
            intl_tokens.Delete,
            intl.formatMessage({
              defaultMessage:
                "Are you sure you want to delete this inbox? All links and QR-codes related to this inbox will stop working.",
              description: "Connect Admin Input: Delete Inbox Alert Message",
            }),
            [
              { text: intl_tokens.Cancel, style: "cancel" },
              {
                text: intl_tokens.Delete,
                style: "destructive",
                onPress: () => {
                  deleteMutation({
                    variables: {
                      id: props.route.params.inboxId,
                    },
                    onCompleted: () => {
                      resetState();
                      props.navigation.goBack();
                    },
                  });
                },
              },
            ]
          );
        },
      },
    ],
    [
      Alert,
      deleteMutation,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Delete,
      props.route.params.inboxId,
      props.navigation,
    ]
  );

  useNavigationHeader({
    type: "SAVE",
    onCancel: props.navigation.goBack,
    onSave: onSave,
    saveLoading: saveLoading,
    disabled: !isDirty,
    title: "",
    defaultContentStyle: true,
    more: moreButtonItems,
  });
  usePreventRemove(isDirty && !deleteLoading, (e) => {
    showCancelWarningAlert(() => {
      props.navigation.dispatch(e.data.action);
    });
  });

  return (
    <MasterDetailOnWeb
      webPreview={
        <WebPreview isDirty={isDirty} publicUrl={publicUrl} saveKey={saveKey} />
      }
      webHeader={
        <Header
          title=""
          headerStyle={{
            backgroundColor: theme.surface,
          }}
          modal={true}
          headerStatusBarHeight={0}
          headerTitleAlign="center"
          headerLeft={() => (
            <HeaderButton type="CANCEL" onPress={props.navigation.goBack} />
          )}
          headerRight={() => (
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <Box spaceRight={"none"} style={{ justifyContent: "center" }}>
                <MoreButton items={moreButtonItems}>
                  {(onOpen) => (
                    <HeaderButton
                      color="text-primary"
                      type="MORE"
                      onPress={onOpen}
                    />
                  )}
                </MoreButton>
              </Box>

              {saveLoading ? (
                <HeaderButtonLoading />
              ) : (
                <HeaderButton
                  type="SAVE"
                  disabled={!isDirty}
                  onPress={() => {
                    onSave();
                  }}
                />
              )}
            </View>
          )}
        />
      }
    >
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        bottomOffset={30}
      >
        <EntityHeader>
          <SvgFromXml
            style={{ width: 100, height: 100 }}
            xml={data.connectInbox.publicQRCode?.svg}
          />
          <Box style={{ flexDirection: "row" }} gap="small" spaceTop="small">
            <EntityHeader.Button onPress={showPreview}>
              <FormattedMessage
                defaultMessage="View Page"
                description="Connect Admin Input: Preview Page Button Title"
              />
            </EntityHeader.Button>
            <EntityHeader.Button onPress={onShare}>
              {Platform.OS === "web" ? (
                <FormattedMessage
                  defaultMessage="Download QR Code"
                  description="Connect Admin Input: Download QR Code Button Title"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Share QR Code"
                  description="Connect Admin Input: Share QR Code Button Title"
                />
              )}
            </EntityHeader.Button>

            <EntityHeader.Button
              onPress={async () => {
                if (Platform.OS === "web") {
                  navigator.clipboard.writeText(publicUrl);
                } else {
                  await Clipboard.setUrlAsync(publicUrl);
                }
              }}
            >
              <FormattedMessage
                defaultMessage="Copy link"
                description="Connect Admin Input: Copy link Button Title"
              />
            </EntityHeader.Button>
          </Box>
        </EntityHeader>
        <Box space="medium">
          <ListItem border="border-primary" isFirst gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Audience"
                description="Connect Admin Input: Audience Title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="Who is going to use this inbox?"
                description="Connect Admin Input: Audience Sub Title"
              />
            </ListItem.SubTitle>

            <CategorySelectorCompact>
              <CategorySelectorCompact.Item
                label={customerAudience}
                onPress={() => {
                  setAudience("CUSTOMERS");
                }}
                selected={audience === "CUSTOMERS"}
              />
              <CategorySelectorCompact.Item
                label={employeeAudience}
                onPress={() => {
                  setAudience("EMPLOYEES");
                }}
                selected={audience === "EMPLOYEES"}
              />
            </CategorySelectorCompact>
          </ListItem>
          <AHSeparator />
          <ListItem border="border-primary" gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Todo List"
                description="Connect Admin Input: Todo List Title"
              />
            </ListItem.Title>

            <View style={{ flexDirection: "row" }}>
              <DialogButton
                indicatorIcon={getTodoCategoryIcon(
                  selectedTodoCategory?.icon ?? "Check"
                )}
                mode="active"
                onPress={() => setFeaturesOpen("todoCategory")}
              >
                {selectedTodoCategory?.title}
              </DialogButton>
            </View>
          </ListItem>

          <AHSeparator />
          <ListItem border="border-primary" gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Teams and Tags"
                description="Connect Admin Input: Todo List Title"
              />
            </ListItem.Title>

            <Box style={{ flexDirection: "row" }} gap="small">
              <DialogButtonTeams
                teams={selectedTeams}
                onPress={() => setFeaturesOpen("teams")}
              />
              {(selectedTodoCategory?.tags.length ?? 0) > 0 ? (
                <TodoToolbarButtonTags
                  tags={selectedTags}
                  onPress={() => setFeaturesOpen("tags")}
                />
              ) : null}
            </Box>
          </ListItem>
          <AHSeparator />
          <ListItem border="border-primary" gap="standard" isLast>
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Name"
                description="Connect Admin Input: Name Title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="The name of the inbox that will be displayed to the user. "
                description="Connect Admin Input: name sub title"
              />
            </ListItem.SubTitle>

            <Input
              value={_title ?? data.connectInbox.customTitle ?? ""}
              onChangeText={setTitle}
              placeholder={selectedTodoCategory?.title ?? ""}
              placeholderColor="text-primary"
            />
          </ListItem>
        </Box>
        <Box space="medium">
          <ListItem border="border-primary" isFirst gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Welcome message"
                description="Connect Admin Input: Welcome message Title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="Short greeting that will be displayed in larger text"
                description="Connect Admin Input: Welcome message Sub Title"
              />
            </ListItem.SubTitle>
            <Input
              value={_welcomeMessage ?? data.connectInbox.welcomeMessage ?? ""}
              onChangeText={setWelcomeMessage}
              placeholder={welcomeMessagePlaceholder}
            />
          </ListItem>

          <AHSeparator />
          <ListItem border="border-primary" gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Description"
                description="Connect Admin Input: Description Title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="Description that can be slightly longer, will be displayed in smaller text"
                description="Connect Admin Input: Description Sub Title"
              />
            </ListItem.SubTitle>

            <Input
              value={_description ?? data.connectInbox.description ?? ""}
              onChangeText={setDescription}
              placeholder={descriptionPlaceholder}
              multiline
            />
          </ListItem>
          <AHSeparator />
          <ListItem border="border-primary" isLast gap="standard">
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Thank-you message"
                description="Connect Admin Input: Success Message Title"
              />
            </ListItem.Title>
            <ListItem.SubTitle>
              <FormattedMessage
                defaultMessage="Message that will be displayed after the user has submitted their input"
                description="Connect Admin Input: Success Message Sub Title"
              />
            </ListItem.SubTitle>

            <Input
              value={_successMessage ?? data.connectInbox.successMessage ?? ""}
              multiline
              onChangeText={setSuccessMessage}
              placeholder={successMessagePlaceholder}
            />
          </ListItem>
        </Box>
        {audience === "CUSTOMERS" ? (
          <>
            <Box space="medium">
              {true ? (
                <>
                  <ListItem
                    border="border-primary"
                    gap="standard"
                    isFirst
                    isLast={!hasContactFields}
                  >
                    <ListItem.Title>
                      <FormattedMessage
                        defaultMessage="Contact Fields"
                        description="Connect Admin Input: Contact Fields Title"
                      />
                    </ListItem.Title>

                    <ContactFormItem
                      onChange={(value) => setContactFormName(value)}
                      value={
                        _contactFormName ?? data.connectInbox.contactFormName
                      }
                    >
                      <FormattedMessage
                        defaultMessage="Name"
                        description="Connect Admin Input: Enable Name Title"
                      />
                    </ContactFormItem>

                    <ContactFormItem
                      onChange={(value) => setContactFormPhone(value)}
                      value={
                        _contactFormPhone ?? data.connectInbox.contactFormPhone
                      }
                    >
                      <FormattedMessage
                        defaultMessage="Phone"
                        description="Connect Admin Input: Enable Phone Number Title"
                      />
                    </ContactFormItem>
                    <ContactFormItem
                      onChange={(value) => setContactFormEmail(value)}
                      value={
                        _contactFormEmail ?? data.connectInbox.contactFormEmail
                      }
                    >
                      <FormattedMessage
                        defaultMessage="Email"
                        description="Connect Admin Input: Enable Email Title"
                      />
                    </ContactFormItem>
                  </ListItem>

                  {hasContactFields ? (
                    <>
                      <AHSeparator />
                      <ListItem border="border-primary" gap="standard" isLast>
                        <ListItem.Title>
                          <FormattedMessage
                            defaultMessage="Contact info prompt"
                            description="Connect Admin Input: Enable Contact Form Title"
                          />
                        </ListItem.Title>
                        <ListItem.SubTitle>
                          <FormattedMessage
                            defaultMessage="Shown above the contact form"
                            description="Connect Admin Input: Enable Contact Form Subtitle"
                          />
                        </ListItem.SubTitle>

                        <Input
                          value={
                            _contactFieldPrompt ??
                            data.connectInbox.contactFormPrompt ??
                            ""
                          }
                          onChangeText={(text) => {
                            setContactFieldPrompt(text);
                          }}
                          multiline
                          numberOfLines={3}
                          placeholder={contactFieldPrompt}
                        />
                      </ListItem>
                    </>
                  ) : null}
                </>
              ) : null}
            </Box>
          </>
        ) : (
          <>
            <Box space="medium">
              <ListItem border="border-primary" isFree gap="standard">
                <ListItem.Title>
                  <FormattedMessage
                    defaultMessage="Collect Employee Identity"
                    description="Connect Admin Input: Employee Enable Contact Form Title"
                  />
                </ListItem.Title>
                <ListItem.SubTitle>
                  <FormattedMessage
                    defaultMessage="The user can provide their name or employee ID"
                    description="Connect Admin Input: Employee Enable Contact Form Subtitle"
                  />
                </ListItem.SubTitle>
                <ContactFormItem
                  onChange={(value) => setContactFormName(value)}
                  value={_contactFormName ?? data.connectInbox.contactFormName}
                >
                  <FormattedMessage
                    defaultMessage="Name or Employee ID"
                    description="Connect Admin Input: Enable Email Title"
                  />
                </ContactFormItem>
              </ListItem>
            </Box>
          </>
        )}
      </KeyboardAwareScrollView>
      <TeamSelectPopover
        teamIds={selectedTeams.map((team) => team.id)}
        visible={featuresOpen === "teams"}
        onClose={() => setFeaturesOpen(null)}
        onSelectAll={() => {
          setSelectedTeamIds([]);
        }}
        onItemPress={(itemId, selected) => {
          setSelectedTeamIds((_prev) => {
            const prev = _prev ?? teamIdsToFilter;
            return selected
              ? prev.filter((id) => id !== itemId)
              : [...prev, itemId];
          });
        }}
        teams={data.store.publicTeams}
      />
      <TodoTagsDialog
        categoryTags={selectedTodoCategory?.tags ?? []}
        selectedTagIds={selectedTags.map((tag) => tag.id)}
        setSelectedTagIds={(tags) => {
          setSelectedTagIds(tags);
        }}
        visible={featuresOpen === "tags"}
        onClose={() => setFeaturesOpen(null)}
      />
      <TodoCategoryDialog
        selectedTodoCategoryId={selectedTodoCategoryId}
        setSelectedTodoCategoryId={(id) => {
          setTodoCategoryId(id);
          setSelectedTagIds(null);
          setTitle(null);
        }}
        visible={featuresOpen === "todoCategory"}
        onClose={() => setFeaturesOpen(null)}
        todoCategories={data.store.todoCategories.map((category) => ({
          id: category.id,
          icon: category.icon ?? "Check",
          title: category.title,
        }))}
      />
    </MasterDetailOnWeb>
  );
}
function ContactFormItem(props: {
  children: React.ReactNode;
  value: ContactFieldConfig;
  onChange: (value: ContactFieldConfig) => void;
}) {
  const isSelected = props.value === "REQUIRED" || props.value === "OPTIONAL";
  return (
    <ListItem
      isFree
      border="border-primary"
      onPress={() => {
        if (isSelected) {
          props.onChange("DISABLED");
        } else {
          props.onChange("REQUIRED");
        }
      }}
      left={<ListItem.MultiSelectIndicator selected={isSelected} />}
      right={
        isSelected ? (
          <ListItem.RequiredSelectIndicator
            onPress={() => {
              props.onChange(
                props.value === "REQUIRED" ? "OPTIONAL" : "REQUIRED"
              );
            }}
            label={
              <FormattedMessage
                defaultMessage="Required*"
                description="Connect Admin Input: Required Label"
              />
            }
            selected={props.value === "REQUIRED"}
          />
        ) : null
      }
    >
      <ListItem.Title>{props.children}</ListItem.Title>
    </ListItem>
  );
}

function MasterDetailOnWeb(props: {
  children: React.ReactNode;
  webPreview: React.ReactNode;
  webHeader: React.ReactNode;
}) {
  if (Platform.OS === "web") {
    return (
      <Box
        style={{ flex: 1, flexDirection: "row" }}
        color="surface-background-light"
      >
        <View style={{ minWidth: 450, flexBasis: 450 }}>
          {props.webHeader}
          {props.children}
        </View>
        <View style={{ flex: 1 }}>{props.webPreview}</View>
      </Box>
    );
  }
  return props.children;
}
